import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '../lib/cn';
import { initials } from '../lib/format';
import styles from './Feedback.module.css';

export function Spinner({ size = 'md', label = 'Loading', className }) {
  return (
    <span className={cn(styles.spinner, styles[`spinner-${size}`], className)} role="status">
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

/** Centered spinner for whole-page/section loading. */
export function PageLoader({ label = 'Loading…', minHeight = '50vh' }) {
  return (
    <div className={styles.pageLoader} style={{ minHeight }}>
      <Spinner size="lg" label={label} />
      <p className={styles.pageLoaderText}>{label}</p>
    </div>
  );
}

export function Skeleton({ width, height = 14, radius, className, style }) {
  return (
    <span
      className={cn(styles.skeleton, className)}
      style={{ width, height, borderRadius: radius, ...style }}
      aria-hidden="true"
    />
  );
}

export function EmptyState({ icon, title, description, action, className, compact = false }) {
  return (
    <div className={cn(styles.empty, compact && styles.emptyCompact, className)}>
      {icon && <div className={styles.emptyIcon}>{icon}</div>}
      {title && <h3 className={styles.emptyTitle}>{title}</h3>}
      {description && <p className={styles.emptyText}>{description}</p>}
      {action && <div className={styles.emptyAction}>{action}</div>}
    </div>
  );
}

const ALERT_ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle };

/** Inline callout. tone: info | success | warning | danger | neutral */
export function Alert({ tone = 'info', title, children, icon, action, className }) {
  const Icon = icon === false ? null : icon || ALERT_ICONS[tone] || Info;
  return (
    <div className={cn(styles.alert, styles[`alert-${tone}`], className)} role={tone === 'danger' ? 'alert' : undefined}>
      {Icon && <Icon className={styles.alertIcon} aria-hidden="true" />}
      <div className={styles.alertBody}>
        {title && <p className={styles.alertTitle}>{title}</p>}
        {children && <div className={styles.alertText}>{children}</div>}
      </div>
      {action && <div className={styles.alertAction}>{action}</div>}
    </div>
  );
}

/** tone: neutral | info | success | warning | danger | accent | dark */
export function Badge({ tone = 'neutral', size = 'md', icon, className, children }) {
  return (
    <span className={cn(styles.badge, styles[`badge-${tone}`], styles[`badge-${size}`], className)}>
      {icon && <span className={styles.badgeIcon} aria-hidden="true">{icon}</span>}
      {children}
    </span>
  );
}

export function Avatar({ firstName, lastName, size = 36, className }) {
  return (
    <span
      className={cn(styles.avatar, className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-hidden="true"
    >
      {initials(firstName, lastName)}
    </span>
  );
}

/** Small "label: value" row used in summaries. */
export function KeyValue({ label, value, strong = false, className }) {
  return (
    <div className={cn(styles.kv, strong && styles.kvStrong, className)}>
      <span className={styles.kvLabel}>{label}</span>
      <span className={styles.kvValue}>{value}</span>
    </div>
  );
}
